import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Paper,
  Avatar,
  Typography,
  Button,
  Box,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions
} from '@mui/material';
import {
  Edit,
  LocationOn,
  CalendarToday,
  Verified,
  Person,
  PhotoLibrary
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import PostCard from '../components/Posts/PostCard';
import axios from 'axios';
import { getApiUrl } from '../utils/api';

const Profile = () => {
  const { username } = useParams();
  const { user: currentUser, updateProfile } = useAuth();
  const [profileData, setProfileData] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({
    fullName: '',
    bio: '',
    location: ''
  });
  const [following, setFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);

  const isOwnProfile = currentUser?.username === username;

  useEffect(() => {
    fetchProfile();
  }, [username]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await axios.get(getApiUrl(`/api/users/${username}`));
      setProfileData(response.data.user);
      setPosts(response.data.posts);
      
      if (currentUser && response.data.user.followers) {
        setFollowing(response.data.user.followers.some(f => f.id === currentUser.id));
      }
    } catch (err) {
      console.error('Error fetching profile:', err);
      setError('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleFollow = async () => {
    if (!currentUser) return;
    
    try {
      setFollowLoading(true);
      const response = await axios.post(getApiUrl(`/api/users/${profileData.id}/follow`));
      setFollowing(response.data.isFollowing);
      
      // Update follower count
      setProfileData(prev => ({
        ...prev,
        followers: response.data.isFollowing 
          ? [...prev.followers, { id: currentUser.id }]
          : prev.followers.filter(f => f.id !== currentUser.id)
      }));
    } catch (err) {
      console.error('Error following user:', err);
    } finally {
      setFollowLoading(false);
    }
  };

  const handleEditProfile = () => {
    setEditFormData({
      fullName: profileData.fullName || '',
      bio: profileData.bio || '',
      location: profileData.location || ''
    });
    setEditDialogOpen(true);
  };

  const handleSaveProfile = async () => {
    try {
      const formData = new FormData();
      formData.append('fullName', editFormData.fullName);
      formData.append('bio', editFormData.bio);
      formData.append('location', editFormData.location);

      const result = await updateProfile(formData);
      if (result.success) {
        setProfileData(prev => ({
          ...prev,
          ...editFormData
        }));
        setEditDialogOpen(false);
      }
    } catch (err) {
      console.error('Error updating profile:', err);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!profileData) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">User not found</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, pb: 4 }}>
      {/* Profile Header */}
      <Paper elevation={3} sx={{ p: 4, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item>
            <Avatar
              src={profileData.profilePicture}
              alt={profileData.fullName}
              sx={{ width: 120, height: 120 }}
            >
              {profileData.fullName?.charAt(0)}
            </Avatar>
          </Grid>
          <Grid item xs>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography variant="h4" component="h1">
                {profileData.fullName}
              </Typography>
              {profileData.isVerified && (
                <Verified sx={{ ml: 1, color: 'primary.main' }} />
              )}
            </Box>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              @{profileData.username}
            </Typography>
            
            {profileData.bio && (
              <Typography variant="body1" paragraph>
                {profileData.bio}
              </Typography>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              {profileData.location && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <LocationOn sx={{ fontSize: '1rem', mr: 0.5, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    {profileData.location}
                  </Typography>
                </Box>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CalendarToday sx={{ fontSize: '1rem', mr: 0.5, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  Joined {new Date(profileData.createdAt).toLocaleDateString()}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
              <Typography variant="body2">
                <strong>{profileData.totalTrips}</strong> Trips
              </Typography>
              <Typography variant="body2">
                <strong>{profileData.followers?.length || 0}</strong> Followers
              </Typography>
              <Typography variant="body2">
                <strong>{profileData.following?.length || 0}</strong> Following
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 1 }}>
              {isOwnProfile ? (
                <Button
                  variant="outlined"
                  startIcon={<Edit />}
                  onClick={handleEditProfile}
                >
                  Edit Profile
                </Button>
              ) : currentUser ? (
                <Button
                  variant={following ? "outlined" : "contained"}
                  onClick={handleFollow}
                  disabled={followLoading}
                >
                  {following ? 'Unfollow' : 'Follow'}
                </Button>
              ) : null}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabs */}
      <Paper elevation={1} sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => setTabValue(newValue)}
          variant="fullWidth"
        >
          <Tab icon={<PhotoLibrary />} label="Posts" />
          <Tab icon={<Person />} label="About" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {tabValue === 0 && (
        <Grid container spacing={3}>
          {posts.length === 0 ? (
            <Grid item xs={12}>
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No posts yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {isOwnProfile 
                    ? "Share your first trip experience!"
                    : `${profileData.fullName} hasn't shared any trips yet.`
                  }
                </Typography>
              </Box>
            </Grid>
          ) : (
            posts.map((post) => (
              <Grid item xs={12} key={post._id}>
                <PostCard post={post} />
              </Grid>
            ))
          )}
        </Grid>
      )}

      {tabValue === 1 && (
        <Paper elevation={1} sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            About {profileData.fullName}
          </Typography>
          <Typography variant="body1" paragraph>
            {profileData.bio || 'No bio available.'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Member since {new Date(profileData.createdAt).toLocaleDateString()}
          </Typography>
        </Paper>
      )}

      {/* Edit Profile Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Profile</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Full Name"
            fullWidth
            variant="outlined"
            value={editFormData.fullName}
            onChange={(e) => setEditFormData({ ...editFormData, fullName: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Bio"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={editFormData.bio}
            onChange={(e) => setEditFormData({ ...editFormData, bio: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Location"
            fullWidth
            variant="outlined"
            value={editFormData.location}
            onChange={(e) => setEditFormData({ ...editFormData, location: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveProfile} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Profile;
